import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useEditor } from "../../../../../../App.jsx";
import { IconTrash, IconUsers } from "../../../../../Customiz3r/components/ToolSelectorTop/Icons.jsx";
import { EyeIcon } from "@heroicons/react/20/solid";
import { confirmDelete, showAlert } from "/src/components/HelperAlerts/HelpersAlerts.jsx";
import { getThemeContext } from "../../../../../../components/providers/ThemeProvider.jsx";
import {
    File,
    FileText,
    FileImage,
    FileVideo,
    FileAudio,
} from 'lucide-react'
import AttachmentsList from "./components/AttachmentsList.jsx";

const ClienteEditor = () => {
    const [cliente, setCliente] = useState({
        nombre: '',
        apellido: '',
        email: '',
        status: 'LEAD',
        user: '',                // ID of related user
        telefonos: [],
        direccion: '',
        redes_sociales: {},
    });
    const [attachments, setAttachments] = useState([]);
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [attachmentDesc, setAttachmentDesc] = useState('');
    const [users, setUsers] = useState([]);            // List of all users
    const [newTelefono, setNewTelefono] = useState('');
    const [error, setError] = useState(null);
    const { stateEditor, setStateEditor } = useEditor();
    const isNew = stateEditor?.editorID === 'add';
    const theme = getThemeContext();

    // Load client and users
    useEffect(() => {
        // Fetch users for select
        axios.get('/list_users/').then(({ data }) => {
            setUsers(data.results || data);
        });

        axios.get(`/clients/${stateEditor.editorID}/attachments/`)
            .then(({ data }) => {
                // console.log('Adjuntos cargados:', data);
                // si viene paginado, usamos data.results; si no, data directamente
                const arr = Array.isArray(data) ? data : data.results || []
                setAttachments(arr)
            }).catch(() => setAttachments([]));

        if (!isNew && stateEditor?.editorID) {
            axios.get(`/clients/${stateEditor.editorID}/`).then(({ data }) => {
                const telefonosArr = (data.telefonos || []).map(t => t.numero);
                const redesObj = {};
                (data.redes_sociales || []).forEach(r => { redesObj[r.plataforma] = r.url; });

                setCliente({
                    nombre: data.nombre,
                    apellido: data.apellido || '',
                    email: data.email,
                    status: data.status || 'LEAD',
                    user: data.user || '',
                    telefonos: telefonosArr,
                    direccion: data.direccion || '',
                    redes_sociales: redesObj,
                });
            }).catch(() => setError('No se pudo cargar los datos.'));
        } else {
            setCliente(prev => ({ ...prev, telefonos: [], status: 'LEAD', user: '' }));
        }
    }, [stateEditor.editorID, isNew]);

    // Handlers
    const handleChange = e => {
        const { name, value } = e.target;
        setCliente(prev => ({ ...prev, [name]: value }));
    };

    const handleAddTelefono = () => {
        const tel = newTelefono.trim();
        if (tel && !cliente.telefonos.includes(tel)) {
            setCliente(prev => ({ ...prev, telefonos: [...prev.telefonos, tel] }));
            setNewTelefono('');
        }
    };

    const handleRemoveTelefono = idx => {
        setCliente(prev => ({ ...prev, telefonos: prev.telefonos.filter((_, i) => i !== idx) }));
    };

    const handleSocialChange = (e, plataforma) => {
        const { value } = e.target;
        setCliente(prev => ({ ...prev, redes_sociales: { ...prev.redes_sociales, [plataforma]: value } }));
    };

    // Build payload including related user
    const buildPayload = () => ({
        nombre: cliente.nombre,
        apellido: cliente.apellido,
        email: cliente.email,
        status: cliente.status,
        user: cliente.user,
        direccion: cliente.direccion,
        telefonos: cliente.telefonos.map(n => ({ tipo: 'MOBILE', numero: n })),
        redes_sociales: Object.entries(cliente.redes_sociales)
            .filter(([, url]) => url?.trim())
            .map(([pl, url]) => ({ plataforma: pl, url: url.trim() })),
    });

    // Submit handler
    const submit = () => {
        const payload = buildPayload();
        const req = isNew
            ? axios.post('/clients/', payload)
            : axios.put(`/clients/${stateEditor.editorID}/`, payload);

        req.then(({ status, data }) => {
            if (status === 201 || status === 200) {
                showAlert({
                    title: isNew ? 'Cliente creado' : 'Cliente actualizado',
                    text: '¿Volver a la lista de clientes?',
                    icon: 'success',
                    theme,
                    showCancelButton: true,
                    confirmButtonText: 'Sí',
                    cancelButtonText: 'No',
                }).then(result => {
                    if (result.isConfirmed) setStateEditor({ editor: false, editorID: null, editorType: null });
                    else setStateEditor({ editor: true, editorID: data.id, editorType: 'cliente_view' });
                });
            }
        }).catch(() => {
            showAlert({ title: 'Error', text: 'Error al guardar el cliente.', icon: 'error', theme });
            setError('Error al guardar.');
        });
    };

    // Delete handler
    const onDelete = async () => {
        if (!stateEditor.editorID || isNew) return;
        const confirmed = await confirmDelete({ title: 'Borrar cliente', text: 'Esta acción no se puede revertir', confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar', theme });
        if (!confirmed) return;

        axios.delete(`/clients/${stateEditor.editorID}/`).then(() => {
            showAlert({ title: 'Cliente borrado', text: 'El cliente se ha borrado correctamente.', icon: 'success', theme });
            setStateEditor({ editor: false, editorID: null, editorType: null });
        }).catch(() => setError('Error al borrar'));
    };

    // Subida de un solo fichero
    const handleUploadAttachment = () => {
        const form = new FormData();
        form.append('file', attachmentFile);
        form.append('descripcion', attachmentDesc);

        axios.post(
            `/clients/${stateEditor.editorID}/attachments/`,
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
            .then(({ data }) => {
                setAttachments(prev => [...prev, data]);
                setAttachmentFile(null);
                setAttachmentDesc('');
                showAlert({ title: '¡Adjunto subido!', icon: 'success', theme });
            })
            .catch(() => {
                showAlert({ title: 'Error', text: 'No se pudo subir el archivo.', icon: 'error', theme });
            });
    };

    // Borrar un adjunto
    const handleDeleteAttachment = id => {
        axios.delete(`/clients/${stateEditor.editorID}/attachments/${id}/`)
            .then(() => {
                setAttachments(prev => prev.filter(a => a.id !== id));
                showAlert({ title: 'Adjunto eliminado', icon: 'success', theme });
            })
            .catch(() => {
                showAlert({ title: 'Error', text: 'No se pudo borrar.', icon: 'error', theme });
            });
    };

    const [viewMode, setViewMode] = useState('carousel');


    const handleView = () => {
        // console.log('handleView', cliente.id);
        setStateEditor({
            ...stateEditor,
            editor: true,
            // editorID: cliente.id,
            editorType: 'cliente_view',
        });

    }
    return (
        <div className="flex flex-col h-full glassCard p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <div>
                    <button onClick={() => setStateEditor({ editor: false, editorID: null, editorType: null })} className="btn mr-2">
                        <IconUsers className="w-5 h-5 inline" /> <span className="ml-1">Volver</span>
                    </button>
                    {!isNew && (
                        <button onClick={handleView} className="btn">
                            <EyeIcon className="w-5 h-5 inline" /> <span className="ml-1">Ver</span>
                        </button>
                    )}
                </div>
                <h2 className="text-3xl font-semibold">{isNew ? 'Añadir cliente' : 'Editar cliente'}</h2>
                <div />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-2 space-y-6">
                {error && <p className="text-red-600 mb-2">{error}</p>}

                {/* Información Personal */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-neutral-700">Información Personal</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-neutral-600 mb-1">Nombre *</label>
                            <input
                                name="nombre"
                                value={cliente.nombre}
                                onChange={handleChange}
                                placeholder="Introduce el nombre"
                                className="input bg-base-300 w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-neutral-600 mb-1">Apellido</label>
                            <input
                                name="apellido"
                                value={cliente.apellido}
                                onChange={handleChange}
                                placeholder="Introduce el apellido"
                                className="input bg-base-300 w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* Información de Contacto */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-neutral-700">Información de Contacto</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-neutral-600 mb-1">Email *</label>
                            <input
                                name="email"
                                type="email"
                                value={cliente.email}
                                onChange={handleChange}
                                placeholder="correo@ejemplo.com"
                                className="input bg-base-300 w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-neutral-600 mb-1">Dirección</label>
                            <input
                                name="direccion"
                                value={cliente.direccion}
                                onChange={handleChange}
                                placeholder="Introduce la dirección"
                                className="input bg-base-300 w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* Teléfonos */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-neutral-700">Teléfonos</h3>
                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                            {cliente.telefonos.map((tel, idx) => (
                                <span key={idx}
                                      className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                    {tel}
                                    <button type="button" onClick={() => handleRemoveTelefono(idx)}
                                            className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                                </span>
                            ))}
                        </div>
                        <input
                            type="tel"
                            value={newTelefono}
                            onChange={e => setNewTelefono(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' ? (e.preventDefault(), handleAddTelefono()) : null}
                            placeholder="Introduce teléfono y presiona Enter"
                            className="input bg-base-300 w-full"
                        />
                    </div>
                </div>

                {/* Gestión y Estado */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-neutral-700">Gestión y Estado</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-neutral-600 mb-1">Usuario Asociado</label>
                            <select
                                name="user"
                                value={cliente.user}
                                onChange={handleChange}
                                className="input bg-base-300 w-full"
                            >
                                <option value="">-- Selecciona un usuario --</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.public_username || u.email}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-neutral-600 mb-1">Estado del Cliente</label>
                            <select
                                name="status"
                                value={cliente.status}
                                onChange={handleChange}
                                className="input bg-base-300 w-full"
                            >
                                <option value="LEAD">Lead</option>
                                <option value="ACTIVE">Activo</option>
                                <option value="INACTIVE">Inactivo</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Redes Sociales */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-neutral-700">Redes Sociales</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {['FACEBOOK', 'TWITTER', 'INSTAGRAM', 'LINKEDIN', 'OTHER'].map(pl => (
                            <div key={pl}>
                                <label className="block text-sm font-medium text-neutral-600 mb-1">
                                    {pl === 'OTHER' ? 'Otra Red Social' : pl.charAt(0) + pl.slice(1).toLowerCase()}
                                </label>
                                <input
                                    placeholder={`URL de ${pl === 'OTHER' ? 'la red social' : pl.toLowerCase()}`}
                                    value={cliente.redes_sociales[pl] || ''}
                                    onChange={e => handleSocialChange(e, pl)}
                                    className="input bg-base-300 w-full"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Adjuntos */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-neutral-700">Adjuntos</h3>
                    <AttachmentsList
                        attachments={attachments}
                        handleDeleteAttachment={handleDeleteAttachment}
                    />
                </div>
            </div>

            {/* Subida de adjuntos */}
            {!isNew && (
                <div className="mt-6 border-t pt-4 space-y-4 px-2">
                    <h4 className="text-md font-medium text-neutral-700">Subir Nuevo Adjunto</h4>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-neutral-600 mb-1">Archivo</label>
                            <input
                                type="file"
                                onChange={e => setAttachmentFile(e.target.files[0])}
                                className="input bg-base-300 w-full"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-neutral-600 mb-1">Descripción</label>
                            <input
                                type="text"
                                placeholder="Descripción del archivo (opcional)"
                                value={attachmentDesc}
                                onChange={e => setAttachmentDesc(e.target.value)}
                                className="input bg-base-300 w-full"
                            />
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={handleUploadAttachment}
                                disabled={!attachmentFile}
                                className="btn btn-primary w-full sm:w-auto"
                            >
                                Subir Adjunto
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="flex-shrink-0 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 mt-6 pt-4 border-t">
                <button
                    onClick={submit}
                    className="btn btn-secondary w-full sm:w-auto"
                >
                    {isNew ? 'Crear Cliente' : 'Guardar Cambios'}
                </button>
                {!isNew && (
                    <button
                        onClick={onDelete}
                        className="btn bg-red-600 hover:bg-red-800 flex items-center justify-center w-full sm:w-auto"
                    >
                        <IconTrash className="w-5 h-5"/>
                        <span className="ml-1">Eliminar</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default ClienteEditor;